import React, { useState, useEffect } from 'react';
import { AlertCircle, Edit3, CheckCircle, Package, Info } from 'lucide-react';

function IssuesTab({ token, activeShopId, setSelectedItem }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const shopParam = activeShopId ? `&shopId=${activeShopId}` : "";
      const res = await fetch(`/api/inventory/audit-errors?t=${Date.now()}${shopParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      console.log('🐞 Audit Errors Received:', data.length);
      setIssues(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    fetchIssues();
  }, [activeShopId]);

  const handleFixPrice = async (item, suggestedPrice = null) => {
    const field = (item.status === 'sold' && (item.sale_price === 0 || !item.sale_price)) ? 'sale_price' : 'cost_price';
    const label = field === 'sale_price' ? 'Sotuv narxi' : 'Tan narxi';

    let price;
    if (suggestedPrice) {
      price = suggestedPrice;
    } else {
      price = prompt(`${item.product_name} uchun ${label}ni kiriting:`, item[field] || "");
      if (price === null || price === "") return;
    }

    try {
      const shopParam = activeShopId ? `&shopId=${activeShopId}` : "";
      const res = await fetch(`/api/inventory/fix-price${shopParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: item.id,
          [field]: Number(price)
        })
      });
      if (res.ok) {
        setIssues(issues.filter(i => i.id !== item.id));
      }
    } catch (e) { alert("Xato yuz berdi"); }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="bg-amber-500/10 border border-amber-500/20 p-6 rounded-[30px] flex items-center gap-4">
        <Info className="text-amber-500" size={32} />
        <div>
          <h3 className="text-xl font-black text-white">Ma'lumotlar Auditi</h3>
          <p className="text-slate-400 font-bold text-sm">Quyidagi mahsulotlarda narxlar yoki nomlar yetishmayapti. Ularni to'g'rilash foyda hisob-kitobini aniq qiladi.</p>
        </div>
      </div>

      <div className="bg-slate-900 rounded-[45px] shadow-2xl border border-blue-500/10 overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="text-[11px] font-black uppercase text-slate-500 bg-black/20">
              <th className="px-10 py-6 tracking-widest">Mahsulot / Kod</th>
              <th className="px-10 py-6 tracking-widest">Muammo turi</th>
              <th className="px-10 py-6 text-right tracking-widest">Harakat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {issues.length === 0 && !loading && (
              <tr>
                <td colSpan="3" className="px-10 py-20 text-center text-slate-500 font-black text-xl">
                  <div className="flex flex-col items-center gap-4 opacity-30">
                    <CheckCircle size={64} />
                    Hozircha xatoliklar yo'q
                  </div>
                </td>
              </tr>
            )}
            {issues.map((item, idx) => {
              const needsSalePrice = item.status === 'sold' && (item.sale_price === 0 || !item.sale_price);
              const needsCostPrice = (item.cost_price === 0 || !item.cost_price);
              const needsName = item.product_name.startsWith('GTIN:');
              const isExternal = item.sale_source === 'external';

              return (
                <tr key={idx} className="hover:bg-white/5 transition group cursor-pointer" onClick={() => setSelectedItem(item)}>
                  <td className="px-10 py-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-500 border border-white/5">
                         <Package size={18}/>
                      </div>
                      <div>
                        <div className="font-black text-slate-200">{item.product_name}</div>
                        {item.sold_at && (
                           <div className="text-[10px] text-slate-400 font-bold mb-1">Sotilgan sana: {new Date(item.sold_at).toLocaleString()}</div>
                        )}
                        <div className="text-[10px] font-bold mt-1 flex flex-col gap-0.5">
                           <span className="text-blue-400 font-mono">ID (31): {(item.marking_code || '').substring(0, 31)}</span>
                           {(item.marking_code || '').length > 31 && (
                             <span className="text-slate-600 font-mono">Sec (91): {(item.marking_code || '').substring(31)}</span>
                           )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-10 py-6">
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2 flex-wrap">
                        {isExternal && (
                          <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter">Tashqi Sotuv (Ghost)</span>
                        )}
                        {needsSalePrice && (
                          <span className="bg-rose-500/10 text-rose-500 border border-rose-500/20 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter">Sotuv Narxi Yo'q</span>
                        )}
                        {needsCostPrice && (
                          <span className="bg-amber-500/10 text-amber-500 border border-amber-500/20 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter">Tan Narxi Yo'q</span>
                        )}
                        {needsName && (
                          <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter">Nomi Aniqlanmagan</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 font-bold italic">
                        {isExternal && "⚠️ Diqqat: Bu tovar 'Asil Belgi'da qoldiqda yo'q, lekin bizda sotilmagan edi. Narxini tekshiring."}
                        {needsSalePrice && !isExternal && "💡 Tuzatish: Asil Belgidan chek raqamini toping va summani kiriting."}
                        {!needsSalePrice && needsCostPrice && "💡 Tuzatish: Invoysdagi kirim narxini yozib qo'ying."}
                      </div>
                      {item.suggested_price && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleFixPrice(item, item.suggested_price); }}
                          className="mt-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 rounded-xl text-[10px] font-black flex items-center gap-2 hover:bg-emerald-500 hover:text-white transition w-fit"
                        >
                          ✨ TAVSIYA: {Number(item.suggested_price).toLocaleString()} s [TASDIQLASH]
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-10 py-6 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleFixPrice(item); }}
                      className="p-3 bg-white/5 text-slate-400 rounded-xl hover:bg-blue-600 hover:text-white transition group-hover:scale-110 active:scale-90"
                    >
                      <Edit3 size={18} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default IssuesTab;
